import { useState } from "react";

const FORM_SUBMIT_ADDR = "https://readdy.ai/api/form/da0r1ko3256a3o8kovv0";

const interests = ["火影忍者", "海贼王", "咒术回战", "鬼灭之刃", "排球少年"];

export default function SubscribeSection() {
  const [status, setStatus] = useState<"idle" | "success" | "error">("idle");
  const [formError, setFormError] = useState("");
  const [selected, setSelected] = useState<string[]>([]);

  const toggleInterest = (name: string) => {
    setSelected((prev) =>
      prev.includes(name) ? prev.filter((n) => n !== name) : [...prev, name]
    );
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setStatus("idle");
    setFormError("");

    const form = event.currentTarget;
    const formData = new FormData(form);

    // 蜜罐字段：被自动填充时静默放行，不提交任何数据
    const honeypot = formData.get("phone_alt")?.toString().trim() || "";
    if (honeypot !== "") {
      setStatus("success");
      form.reset();
      setSelected([]);
      return;
    }

    // 移除空蜜罐字段
    formData.delete("phone_alt");

    // 补充多选兴趣
    if (selected.length > 0) {
      formData.append("interest_ip", selected.join("、"));
    }

    try {
      const res = await fetch(FORM_SUBMIT_ADDR, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });
      const responseText = await res.text();
      let parsed: { code?: string; meta?: { message?: string; detail?: string }; message?: string } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }
      const serverMsg =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || "";

      if (res.ok && parsed?.code === "OK") {
        setStatus("success");
        form.reset();
        setSelected([]);
      } else {
        setStatus("error");
        setFormError(
          serverMsg || "提交失败，请稍后重试或核对你的信息。"
        );
      }
    } catch {
      setStatus("error");
      setFormError("网络异常，请稍后重试。");
    }
  };

  return (
    <section
      id="subscribe"
      className="relative w-full overflow-hidden bg-background-900/60 py-16 md:py-20"
    >
      <div className="absolute inset-0">
        <img
          src="https://readdy.ai/api/search-image?query=stylized%20anime%20background%20of%20glowing%20paper%20lanterns%20and%20falling%20petals%20in%20a%20dark%20night%20sky%2C%20warm%20amber%20and%20crimson%20bokeh%2C%20dreamy%20and%20inviting%20atmosphere%2C%20bold%20manga%20aesthetic%2C%20cinematic%20lighting%2C%20high%20contrast%2C%20decorative%20artistic%20background&width=1600&height=700&seq=subscribe-bg&orientation=landscape"
          alt="同人动漫订阅与投稿背景"
          title="同人动漫投稿"
          className="h-full w-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-background-950/70"></div>
      </div>

      <div className="relative z-10 mx-auto w-full max-w-5xl px-4 md:px-6">
        <div className="grid grid-cols-1 gap-10 lg:grid-cols-2 lg:items-center">
          <div>
            <span className="inline-flex items-center gap-2 text-sm font-bold uppercase tracking-widest text-accent-400">
              <i className="ri-mail-send-line"></i>
              JOIN US
            </span>
            <h2 className="mt-3 font-heading text-2xl font-black text-foreground-950 md:text-4xl">
              订阅更新，投稿入驻
            </h2>
            <p className="mt-4 text-sm leading-relaxed text-foreground-600 md:text-base">
              留下你的联系方式与感兴趣的IP，第一时间获取最新同人更新；同人创作者也可通过表单提交作品简介，申请入驻展示。
            </p>
            <div className="mt-6">
              <p className="text-sm font-bold text-foreground-800">我关注的IP</p>
              <div className="mt-3 flex flex-wrap gap-2">
                {interests.map((name) => (
                  <button
                    key={name}
                    type="button"
                    onClick={() => toggleInterest(name)}
                    className={`inline-flex items-center gap-1.5 whitespace-nowrap rounded-full border px-3.5 py-1.5 text-sm font-medium transition-colors ${
                      selected.includes(name)
                        ? "border-accent-400 bg-accent-500/20 text-accent-200"
                        : "border-background-300/40 text-foreground-500 hover:border-accent-400/40 hover:text-accent-300"
                    }`}
                  >
                    <i
                      className={`${selected.includes(name) ? "ri-checkbox-circle-fill" : "ri-checkbox-blank-circle-line"} text-sm`}
                    ></i>
                    {name}
                  </button>
                ))}
              </div>
            </div>
          </div>

          <div className="rounded-lg bg-background-800/80 p-6 ring-1 ring-background-200/50 backdrop-blur md:p-8">
            {status === "success" ? (
              <div className="flex flex-col items-center py-10 text-center">
                <span className="flex h-14 w-14 items-center justify-center rounded-full bg-accent-500/20 text-3xl text-accent-400">
                  <i className="ri-check-line"></i>
                </span>
                <h3 className="mt-4 font-heading text-xl font-bold text-foreground-950">
                  提交成功！
                </h3>
                <p className="mt-2 text-sm text-foreground-600">
                  感谢你的关注与支持，我们会尽快与你联系。
                </p>
                <button
                  type="button"
                  onClick={() => setStatus("idle")}
                  className="mt-6 inline-flex items-center gap-2 whitespace-nowrap rounded-md border border-background-300/40 px-4 py-2 text-sm font-medium text-foreground-600 hover:border-accent-400/50 hover:text-accent-300"
                >
                  <i className="ri-refresh-line"></i>
                  再提交一份
                </button>
              </div>
            ) : (
              <form
                data-readdy-form
                name="同人动漫订阅表单"
                onSubmit={handleSubmit}
                className="flex flex-col gap-4"
              >
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <div>
                    <label htmlFor="name" className="mb-1.5 block text-sm font-medium text-foreground-800">
                      你的称呼
                    </label>
                    <input
                      id="name"
                      name="name"
                      type="text"
                      required
                      placeholder="如何称呼你"
                      className="w-full rounded-md border border-background-200/60 bg-background-900/60 px-3.5 py-2.5 text-sm text-foreground-950 outline-none transition-colors placeholder:text-foreground-500 focus:border-accent-400"
                    />
                  </div>
                  <div>
                    <label htmlFor="email" className="mb-1.5 block text-sm font-medium text-foreground-800">
                      联系邮箱
                    </label>
                    <input
                      id="email"
                      name="email"
                      type="email"
                      required
                      placeholder="you@example.com"
                      className="w-full rounded-md border border-background-200/60 bg-background-900/60 px-3.5 py-2.5 text-sm text-foreground-950 outline-none transition-colors placeholder:text-foreground-500 focus:border-accent-400"
                    />
                  </div>
                </div>
                <div>
                  <label htmlFor="message" className="mb-1.5 block text-sm font-medium text-foreground-800">
                    留言 / 作品简介
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    rows={4}
                    maxLength={500}
                    placeholder="想投稿的话，在这里简单介绍你的作品与创作方向～"
                    className="w-full resize-none rounded-md border border-background-200/60 bg-background-900/60 px-3.5 py-2.5 text-sm text-foreground-950 outline-none transition-colors placeholder:text-foreground-500 focus:border-accent-400"
                  ></textarea>
                  <p className="mt-1 text-right text-xs text-foreground-500">
                    <i className="ri-character-recognition-line"></i> 最多500字
                  </p>
                </div>

                {/* 蜜罐字段 */}
                <input
                  type="text"
                  name="phone_alt"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  readOnly
                  className="hp-guard"
                />

                {status === "error" && formError && (
                  <p className="rounded-md bg-primary-500/15 px-3 py-2 text-sm text-primary-300">
                    <i className="ri-error-warning-line mr-1"></i>
                    {formError}
                  </p>
                )}

                <button
                  type="submit"
                  className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-3 text-base font-bold text-background-50 transition-colors hover:bg-primary-600"
                >
                  <i className="ri-send-plane-line"></i>
                  提交信息
                </button>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}